import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { clearAccessFlowLock, type AccessFlow } from "@/lib/access-flow";
import { markSessionExpired, markVoluntarySignout } from "@/lib/session-expired-notice";

/**
 * Contexto opcional para telemetria de cross-flow. Quando informado,
 * registramos um evento em `user_audit_log` ANTES do signOut, enquanto a
 * sessão ainda é válida (RLS exige `auth.uid() = user_id`).
 */
export interface InvalidSessionContext {
  userId: string;
  /** Modo do Guard que detectou a divergência. */
  guardMode: AccessFlow;
  /** Fluxo que estava locado em sessionStorage/localStorage. */
  lockedFlow: AccessFlow | null;
  /** Papéis reais do usuário no backend, para correlação posterior. */
  actualRoles?: { admin?: boolean; venue_owner?: boolean };
  /** Rota que disparou a verificação. */
  route?: string;
  /** Motivo legível: "role_mismatch", "cross_flow_lock", etc. */
  reason: "role_mismatch" | "cross_flow_lock";
}

const logCrossFlowEvent = async (ctx: InvalidSessionContext) => {
  try {
    await supabase.from("user_audit_log").insert({
      user_id: ctx.userId,
      action: "cross_flow_signout",
      details: {
        guard_mode: ctx.guardMode,
        locked_flow: ctx.lockedFlow,
        actual_roles: ctx.actualRoles ?? null,
        route: ctx.route ?? null,
        reason: ctx.reason,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      },
    });
  } catch {
    /* telemetria nunca bloqueia cleanup */
  }
};

/**
 * Encerra uma sessão inválida detectada por um Guard de rota
 * (RoleGuard).
 *
 * Não é "logout voluntário" — é cleanup defensivo. Por isso não navega:
 * o caller já sabe para onde redirecionar. Quando `context` é fornecido,
 * registra um evento `cross_flow_signout` em `user_audit_log` para
 * permitir auditoria de divergências de papel.
 */
export const signOutInvalidSession = async (
  queryClient?: QueryClient,
  context?: InvalidSessionContext
): Promise<void> => {
  if (context) {
    await logCrossFlowEvent(context);
  }
  // Aviso de sessão expirada por motivo cross-flow. Sinalizamos
  // "voluntary" pro listener do useAuth para que ele NÃO sobrescreva
  // com "refresh_failed".
  markVoluntarySignout();
  markSessionExpired("cross_flow");
  try {
    await queryClient?.cancelQueries();
  } catch {
    /* noop */
  }
  try {
    await supabase.auth.signOut();
  } catch {
    /* noop */
  }
  try {
    queryClient?.clear();
  } catch {
    /* noop */
  }
  clearAccessFlowLock();
};
